import { Component, h, Prop, Element, Event, EventEmitter, Listen, State } from '@stencil/core';
import { CmsImageConfig, CmsLinkConfig, CmsNavigationEntry, CmsToggleDetail, Logger } from '../../../global/_types';
import { isMobileViewport, replaceSpecialChars } from '../../../global/_utils';

const OPEN_CLASS = 'open';
const ACTIVE_CLASS = 'active';
const SCROLL_CLASS = 'scrollable';

@Component({
  tag: 'cms-nav-sidebar',
  styleUrl: 'cms-nav-sidebar.scss',
  shadow: true,
})

export class CmsNavSideBar {
  @Element() host: HTMLElement;

  @Prop() config: string | CmsSideBarConfig;
  @Prop() debug: boolean = false;

  @State() dataLoaded: boolean = false;

  /* Config */
  private options: CmsSideBarConfig;
  private navigation: CmsNavSideBarConfig;
  private links: CmsNavigationEntry[];

  private logger: Logger;

  private initialized: boolean = false;
  private flyoutState: boolean = false;
  private flyoutMobileState: boolean = false;
  private flyoutContainer: HTMLDivElement;
  private buttonContainer: HTMLDivElement;

  // Lifecycle
  componentWillLoad() {
    this.logger = new Logger(this.debug, 'CmsNavSideBar');

    this.options =
      this.config && this.config !== '' ?
        typeof (this.config) === 'string' ?
          JSON.parse(this.config) as CmsSideBarConfig :
          this.config
      : null;

    this.navigation = null;
    if (!this.dataLoaded) {
      if (this.options.jsonUrl) {
        fetch(this.options.jsonUrl)
          .then(response => response.json())
          .then(json => {
            this.navigation = replaceSpecialChars(json);
            this.links = this.navigation?.entries;
            this.dataLoaded = true;
          })
          .catch(e => {
            this.showError('Failed to load json data: ' + e);
          });
      } else if (this.options.static) {
        this.navigation = this.options.static;
        this.links = this.navigation?.entries;
        this.dataLoaded = true;
      }
    }
  }

  componentDidLoad() {
    this.logger.trace('CmsNavSideBar loaded');
  }

  private showError(message: string) {
    this.dataLoaded = true;
    this.logger.error(message);
  }

  @Event({
    eventName: 'toggleFlyout',
    composed: true,
    cancelable: true,
    bubbles: false,
  }) toggleFlyout: EventEmitter;

  // Listener
  @Listen('toggleFlyout')
  handleToggleFlyout(event: CustomEvent<CmsToggleDetail>): void {
    event.preventDefault();
    const index = event.detail.index;

    // Show/hide mobile flyout "more"
    if (index == -1) {
      if (this.flyoutState) {
        this.flyoutState = false;
        this.flyoutContainer.parentElement.classList.remove(ACTIVE_CLASS);
        this.flyoutContainer.classList.remove(OPEN_CLASS);
        window.setTimeout(() => {
            this.flyoutMobileState = !this.flyoutMobileState;
            this.buttonContainer.classList.toggle(OPEN_CLASS);
          }, 500);
      } else {
        this.flyoutMobileState = !this.flyoutMobileState;
        this.buttonContainer.classList.toggle(OPEN_CLASS);
      }
      return;
    }

    if (index != 0) {
      return;
    }
    // Show/hide first button flyout
    if (this.flyoutMobileState) {
      this.flyoutMobileState = false;
      this.buttonContainer.classList.remove(OPEN_CLASS);
      window.setTimeout(() => {
        this.toggleButtonFlyout(index);
      }, 500);
    } else {
      this.toggleButtonFlyout(index);
    }
  }

  @Event({
    eventName: 'closeFlyout',
    composed: true,
    cancelable: true,
    bubbles: false,
  }) closeFlyout: EventEmitter;

  // Listener
  @Listen('closeFlyout')
  handleCloseFlyout(event: CustomEvent<CmsToggleDetail>): void {
    event.preventDefault();
    if (event.detail.index < 1) {
      return;
    }

    this.closeButtonFlyout(0);
  }

  private closeButtonFlyout(index: number) {
    if (!this.flyoutContainer) {
      return;
    }
    this.flyoutState = false;
    this.flyoutContainer.classList.remove(OPEN_CLASS);
    window.setTimeout(() => {
      if (!this.flyoutState) {
        this.flyoutContainer.parentElement.classList.remove(ACTIVE_CLASS);
      }
    }, 600);
    this.buttonContainer.querySelector('.menu-item-' + index).querySelector('.sidebar-button').classList.remove(OPEN_CLASS);
  }

  private openButtonFlyout(index: number) {
    if (!this.flyoutContainer) {
      return;
    }
    this.flyoutState = true;
    this.flyoutContainer.classList.add(OPEN_CLASS);
    this.flyoutContainer.parentElement.classList.add(ACTIVE_CLASS);
    this.buttonContainer.querySelector('.menu-item-' + index).querySelector('.sidebar-button').classList.add(OPEN_CLASS);
    if (!this.initialized) {
      this.resizeButtonFlyout();
    }
  }

  private toggleButtonFlyout(index: number) {
    if (!this.flyoutState) {
      this.openButtonFlyout(index);
    } else {
      this.closeButtonFlyout(index);
    }
  }

  /**
   * Make flyout scrollable if window height too small
   * Listen to window resize
   * @private
   */
  @Listen('resize', { target: 'window' })
  private resizeButtonFlyout() {
    if (!this.buttonContainer || !this.flyoutContainer) {
      return;
    }

    const availableSpace = isMobileViewport() ?
      window.innerHeight - this.buttonContainer.offsetHeight - 20 :
      window.innerHeight - this.flyoutContainer.parentElement.offsetTop - 20;

    if (this.flyoutContainer.offsetHeight > availableSpace) {
      this.flyoutContainer.classList.add(SCROLL_CLASS);
      this.flyoutContainer.style.height = availableSpace + 'px';
    } else {
      this.flyoutContainer.classList.remove(SCROLL_CLASS);
      this.flyoutContainer.style.height = null;
    }

    this.initialized = true;
  }

  private getFlyout(): string {
    if (!this.links || this.links.length < 1 || !this.links[0].content) {
      return '';
    }

    const flyout: CmsSideBarFlyoutConfig = this.links[0].content as CmsSideBarFlyoutConfig;
    const contentSize: number = flyout.links?.length > 2 ? flyout.links.length : 2;
    const content = flyout.links?.slice(0,contentSize);
    const contactSize: number = flyout.contact?.length > 3 ? flyout.contact.length : 3;
    const contact = flyout.contact?.slice(0,contactSize);
    return (
      <div class={`flyout cols-${content.length}`} ref={(divElement: HTMLDivElement) => (this.flyoutContainer = divElement)}>
        <label tabindex='0' class='card-button-wrapper' onClick={() => this.toggleFlyout.emit({index: 0})}>
          <span class='card-button'>
            <svg class='card-icon'
                 xmlns='http://www.w3.org/2000/svg'
                 viewBox='0 0 20 20'>
              <path d='M18.5,8.51h-7v-7A1.5,1.5,0,0,0,10,0h0A1.5,1.5,0,0,0,8.5,1.5v7h-7a1.5,1.5,0,0,0,0,3h7v7A1.5,1.5,0,0,0,10,20h0a1.5,1.5,0,0,0,1.5-1.5v-7h7a1.5,1.5,0,0,0,0-3Z'></path>
            </svg>
          </span>
          <span class='card-button-text' role='button' aria-expanded='false' aria-controls='content-toggle-watch'>
            <span class='visualHidden'></span>
          </span>
        </label>
        <div class="flyout-header">
          {flyout.subHeadline ? <h3>{flyout.subHeadline}</h3> : ''}
          {flyout.headline ? <h2>{flyout.headline}</h2> : ''}
        </div>
        {
          content ?
            <div class={`content`}>
              {
                content.map(item =>
                  <div>
                    <cms-link-element debug={this.debug} config={item.link}>
                      <div class='content-inner'>
                        {item.image.url ?
                          <img src={item.image.url} width={item.image.width} height={item.image.height}
                               alt={item.image.caption} />
                          : ''
                        }
                        {item.headline ? <p class="h5">{item.headline}</p> : ''}
                        {item.text ? <p>{item.text}</p> : ''}
                      </div>
                    </cms-link-element>
                  </div>
                )
              }
            </div>
            : ''
        }
        {flyout.text ? <div class='text' innerHTML={flyout.text} /> : ''}
        {
          contact ?
            <div class='contact'>
              {contact.map(item => <cms-link-or-button debug={this.debug} config={item} />)}
            </div>
            : ''
        }
      </div>
    );
  }

  // Render
  render() {
    if (!this.dataLoaded && !this.links) {
      return '';
    }

    return (
      <aside class={`sidebar ${this.navigation?.style ? this.navigation.style : ''}`}>
        { this.getFlyout() }
        <div class={`sidebar-button-container`} ref={(divElement: HTMLDivElement) => (this.buttonContainer = divElement)}>
          <ul class='sidebar-buttons' role="menu" aria-keyshortcuts="Control+I" aria-orientation="vertical" aria-live="polite">
            <li class={`menu-item`} role="menuitem" tabindex="-1" onClick={() => this.toggleFlyout.emit({ index: -1 })}>
              <div class={`sidebar-button grey`}>
                <span class={`icon rat-icon i-arrow-up`} />
                <div class='button-text'>{this.navigation?.labels?.mobileList ? this.navigation.labels.mobileList : 'More'}</div>
              </div>
            </li>
            {
              this.links.map((item: CmsNavigationEntry, index: number) =>
                <li class={`menu-item menu-item-${index}`} role="menuitem" tabindex={index}>
                  <a href={item.link} title={item.title ? item.title : ''}
                     onClick={() => this.toggleFlyout.emit({ index: index })}
                     onMouseOver={() => this.closeFlyout.emit({ index: index })}>
                    <div class={`sidebar-button ${item.color ? item.color : 'grey'}`}>
                      <span class={`icon rat-icon ${item.icon}`} />
                      <div class="button-text">{item.label ? item.label : ''}</div>
                    </div>
                  </a>
                </li>
              )
            }
          </ul>
        </div>
      </aside>
    );
  }
}

export interface CmsSideBarConfig {
  jsonUrl: string | null;
  static: CmsNavSideBarConfig | null;
}

export interface CmsNavSideBarConfig {
  entries: CmsNavigationEntry[] | null;
  labels: {
    mobileList?: string;
  }
  style?: string;
}

export interface CmsSideBarFlyoutConfig {
  headline?: string;
  subHeadline?: string;
  text?: string;
  links?: CmsSideBarFlyoutLinkConfig[];
  contact?: CmsLinkConfig[];
}

export interface CmsSideBarFlyoutLinkConfig {
  image?: CmsImageConfig;
  headline?: string;
  text?: string;
  link?: CmsLinkConfig;
}
